import { useEffect, useState } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { ArrowLeft, Sword } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BuilderPage, CharacterViewPage, LevelUpFlowPage } from '@/features/character-builder';
import { loadCharacter } from '@/services/characterStorage';
import type { Character } from '@/schemas/character';
import FoundationSmokePage from '@/dev/FoundationSmokePage';

function HomePage() {
  return (
    <main className="mx-auto flex min-h-svh max-w-2xl items-center justify-center p-6">
      <Card className="w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-primary/10">
            <Sword className="size-6 text-primary" aria-hidden />
          </div>
          <CardTitle asChild className="text-3xl font-bold tracking-tight">
            <h1>Dungeon World Rebrewed</h1>
          </CardTitle>
          <CardDescription className="text-base">
            Crea, salva e gestisci personaggi per il tuo gruppo di gioco.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap justify-center gap-2 pt-2">
          <Button asChild size="lg">
            <Link to="/builder">Crea il tuo personaggio</Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/character">Apri il tuo personaggio</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/builder" element={<BuilderPage />} />
      <Route path="/character" element={<CharacterViewPage />} />
      <Route path="/character/level-up" element={<LevelUpFlowPage />} />
      {/* FI-46 PR-B: edit retroattivo di un level-up passato (L2+). */}
      <Route path="/character/edit-level-up/:level" element={<LevelUpFlowPage />} />
      {/* FI-46bis: edit retroattivo L1 (builder pre-popolato). */}
      <Route path="/character/edit-l1" element={<EditL1Page />} />
      {/* dev-only: smoke page Fase 2 PR 1 Foundation. */}
      <Route path="/_design/foundation" element={<FoundationSmokePage />} />
    </Routes>
  );
}

/**
 * **FI-46bis** — Wrapper route per `/character/edit-l1`. Carica il PG
 * salvato e instrada al `<BuilderPage editFromCharacter={...}>`.
 * Guardia: se nessun PG salvato → empty state con CTA al builder normal.
 */
function EditL1Page() {
  const [character, setCharacter] = useState<Character | null>(() => loadCharacter());

  useEffect(() => {
    setCharacter(loadCharacter());
  }, []);

  if (!character) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <Button asChild variant="ghost" size="sm" className="mb-4">
          <Link to="/character">
            <ArrowLeft aria-hidden />
            Scheda personaggio
          </Link>
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Nessun personaggio salvato</CardTitle>
            <CardDescription>
              Per modificare il livello 1 di un personaggio devi prima averlo creato e salvato.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/builder">Vai alla creazione personaggio</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return <BuilderPage editFromCharacter={character} />;
}
